export type DateInputResult = { day: string | null; error: string | null };

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/u;
const LOCAL_DAY = /^(\d{2})\.(\d{2})\.(\d{4})$/u;

function daysInMonth(year: number, month: number): number {
  if (month === 2) return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function isDay(value: string): boolean {
  const match = ISO_DAY.exec(value);
  if (!match) return false;
  const year = Number(match[1]),
    month = Number(match[2]),
    day = Number(match[3]);
  return (
    year >= 1 && year <= 9999 && month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month)
  );
}

function isoDay(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function parseDateInput(raw: string): DateInputResult {
  const value = raw.trim();
  if (!value) return { day: null, error: null };
  let day: string | null = null;
  const iso = ISO_DAY.exec(value);
  if (iso) day = value;
  else {
    const local = LOCAL_DAY.exec(value);
    if (local) day = `${local[3]}-${local[2]}-${local[1]}`;
  }
  return day && isDay(day)
    ? { day, error: null }
    : { day: null, error: 'Укажи существующую дату в формате ДД.ММ.ГГГГ.' };
}

export function displayDateInput(iso: string): string {
  if (!isDay(iso)) return iso;
  const [, year, month, day] = ISO_DAY.exec(iso)!;
  return `${day}.${month}.${year}`;
}

export function calendarMonth(day: string): string | null {
  return isDay(day) ? `${day.slice(0, 7)}-01` : null;
}

export function moveCalendarMonth(day: string, delta: number): string | null {
  if (!isDay(day) || !Number.isSafeInteger(delta)) return null;
  const year = Number(day.slice(0, 4)),
    month = Number(day.slice(5, 7));
  const absolute = year * 12 + month - 1 + delta;
  const nextYear = Math.floor(absolute / 12),
    nextMonth = (((absolute % 12) + 12) % 12) + 1;
  return nextYear < 1 || nextYear > 9999 ? null : isoDay(nextYear, nextMonth, 1);
}

function weekdayMondayFirst(day: string): number {
  const [year, month, date] = day.split('-').map(Number);
  const dateObj = new Date(0);
  dateObj.setUTCHours(0, 0, 0, 0);
  dateObj.setUTCFullYear(year, month - 1, date);
  return (dateObj.getUTCDay() + 6) % 7;
}

export function calendarCells(month: string): (string | null)[] {
  if (!/^\d{4}-\d{2}-01$/u.test(month) || !isDay(month)) return Array(42).fill(null);
  const year = Number(month.slice(0, 4)),
    monthNumber = Number(month.slice(5, 7));
  const first = weekdayMondayFirst(month);
  const count = daysInMonth(year, monthNumber);
  return Array.from({ length: 42 }, (_, index) => {
    const day = index - first + 1;
    return day >= 1 && day <= count ? isoDay(year, monthNumber, day) : null;
  });
}

export function dateRangeError(fromRaw: string, toRaw: string): string | null {
  const from = parseDateInput(fromRaw),
    to = parseDateInput(toRaw);
  if (from.error) return from.error;
  if (to.error) return to.error;
  if ((from.day === null) !== (to.day === null)) return 'Укажи обе даты диапазона.';
  if (from.day && to.day && from.day > to.day) return 'Начало диапазона должно быть не позже окончания.';
  return null;
}

export function currencyPrecision(currency: string): number | null {
  if (!/^[A-Z]{3}$/u.test(currency)) return null;
  try {
    const digits = new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
    }).resolvedOptions().maximumFractionDigits;
    return typeof digits === 'number' && Number.isInteger(digits) && digits >= 0 && digits <= 3
      ? digits
      : null;
  } catch {
    return null;
  }
}

export type AmountInputResult = {
  minorUnits: number | null;
  error: string | null;
};
const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER);
const SPACES = /[ \u00a0\u202f]/gu;

export function parseAmountInput(raw: string, currency: string): AmountInputResult {
  const precision = currencyPrecision(currency);
  if (precision === null) return { minorUnits: null, error: 'Неизвестная валюта.' };
  if (raw.length > 256)
    return {
      minorUnits: null,
      error: 'Сумма не должна превышать 256 символов.',
    };
  const value = raw.trim();
  if (!value) return { minorUnits: null, error: null };
  const match = /^([−-]?)(\d+|\d{1,3}(?:[ \u00a0\u202f]\d{3})+)(?:([.,])(\d+))?$/u.exec(value);
  if (!match) return { minorUnits: null, error: 'Укажи сумму числом.' };
  const fraction = match[4] ?? '';
  if (fraction.length > precision)
    return {
      minorUnits: null,
      error: `Для ${currency} допустимо не более ${precision} знаков после запятой.`,
    };
  const scale = 10n ** BigInt(precision);
  const integerDigits = match[2].replace(SPACES, '');
  let minor = BigInt(integerDigits) * scale;
  if (fraction) minor += BigInt(fraction.padEnd(precision, '0'));
  if (match[1]) minor = -minor;
  if (minor > MAX_SAFE || minor < -MAX_SAFE)
    return { minorUnits: null, error: 'Сумма выходит за допустимый диапазон.' };
  return { minorUnits: Number(minor), error: null };
}

export function formatAmountInput(minorUnits: number, currency: string): string {
  const precision = currencyPrecision(currency);
  if (precision === null || !Number.isSafeInteger(minorUnits)) return '';
  const amount = BigInt(minorUnits),
    negative = amount < 0n;
  const magnitude = negative ? -amount : amount,
    scale = 10n ** BigInt(precision);
  const whole = magnitude / scale,
    fraction = magnitude % scale;
  const grouping = new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: 0,
  }).format(whole);
  if (precision === 0) return `${negative ? '−' : ''}${grouping}`;
  const decimal =
    new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    })
      .formatToParts(1)
      .find((part) => part.type === 'decimal')?.value ?? ',';
  return `${negative ? '−' : ''}${grouping}${decimal}${String(fraction).padStart(precision, '0')}`;
}

export type TagsResult = {
  tags: string[];
  remainder: string;
  error: string | null;
};
export function addTags(current: string[], raw: string, maxTags = 8, maxLength = 40): TagsResult {
  const parts = raw
    .split(/[,;\r\n]+/u)
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) return { tags: [...current], remainder: '', error: null };
  if (!Number.isInteger(maxTags) || maxTags < 0 || !Number.isInteger(maxLength) || maxLength < 1)
    return {
      tags: [...current],
      remainder: raw,
      error: 'Лимиты тегов заданы неверно.',
    };
  const tags = [...current];
  const known = new Set(current.map((tag) => tag.toLocaleLowerCase('und')));
  for (const tag of parts) {
    if (tag.length > maxLength)
      return {
        tags: [...current],
        remainder: raw,
        error: `Тег длиннее ${maxLength} символов.`,
      };
    const normalized = tag.toLocaleLowerCase('und');
    if (known.has(normalized)) continue;
    if (tags.length >= maxTags)
      return {
        tags: [...current],
        remainder: raw,
        error: `Можно добавить не больше ${maxTags} тегов.`,
      };
    known.add(normalized);
    tags.push(tag);
  }
  return { tags, remainder: '', error: null };
}
